// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import { logger } from "../utils/logger";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingRole, setLoadingRole] = useState(true);

  // ✅ 1. Load localStorage user FIRST (before Firebase loads)
  useEffect(() => {
    const saved = localStorage.getItem("currentUser");
    if (saved) {
      const parsed = JSON.parse(saved);
      setCurrentUser(parsed);

      if (parsed.role) {
        setCurrentUserRole(normalizeRole(parsed.role));
      }
    }
  }, []);

  // ✅ 2. Firebase Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      let resolvedUser = user;

      // Always attempt to reload the Firebase user
      if (user) {
        try {
          await user.reload();
          resolvedUser = auth.currentUser;
        } catch (reloadError) {
          console.warn("⚠️ AuthContext: Failed to reload user", reloadError);
        }
      }

      logger.log("🔑 AuthContext: Firebase user =", resolvedUser);

      // Save user in state
      setCurrentUser(resolvedUser);
      setLoadingAuth(false);

      // 🔥 If user logs out
      if (!resolvedUser) {
        setCurrentUserRole(null);
        localStorage.removeItem("userRole");
        localStorage.removeItem("currentUser");
        setLoadingRole(false);
        return;
      }

      // ⭐ Save updated user object in localStorage
      localStorage.setItem(
        "currentUser",
        JSON.stringify({
          uid: resolvedUser.uid,
          email: resolvedUser.email,
          name:
            resolvedUser.displayName ||
            resolvedUser.email?.split("@")[0] ||
            "User",
          role:
            normalizeRole(localStorage.getItem("userRole")) ||
            ROLE_VALUE.requester,
        })
      );

      // ⭐ Fetch role from Firestore
      try {
        const userDoc = await getDoc(doc(db, "users", resolvedUser.uid));

        if (userDoc.exists()) {
          const role = normalizeRole(userDoc.data().role);
          setCurrentUserRole(role);

          if (role) {
            localStorage.setItem("userRole", role);
            logger.log("📡 AuthContext: Role fetched from Firestore =", role);

            // ⭐ Update localStorage user with correct role
            const savedUser = JSON.parse(localStorage.getItem("currentUser"));
            if (savedUser) {
              savedUser.role = role;
              localStorage.setItem("currentUser", JSON.stringify(savedUser));
            }
          }
        } else {
          console.warn("⚠️ AuthContext: No Firestore doc for user:", resolvedUser.uid);
          setCurrentUserRole(null);
        }
      } catch (error) {
        console.error("❌ AuthContext: Failed to fetch role from Firestore", error);
      }

      setLoadingRole(false);
    });

    return unsubscribe;
  }, []);


  // Keep role updated in localStorage
  useEffect(() => {
    if (currentUserRole) {
      localStorage.setItem("userRole", currentUserRole);

      const savedUser = localStorage.getItem("currentUser");
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        parsed.role = currentUserRole;
        localStorage.setItem("currentUser", JSON.stringify(parsed));
      }
    } else {
      localStorage.removeItem("userRole");
    }
  }, [currentUserRole]);

  const refreshCurrentUser = async () => {
    if (!auth.currentUser) return null;
    try {
      await auth.currentUser.reload();
      const updatedUser = auth.currentUser;
      setCurrentUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("❌ AuthContext: Failed to refresh current user", error);
      return null;
    }
  };

  // Logout
  const logout = async () => {
    setCurrentUserRole(null);
    localStorage.removeItem("userRole");
    localStorage.removeItem("currentUser");
    await signOut(auth);
    logger.log("🔓 AuthContext: User logged out");
  };

  // App ready only when both states loaded
  const ready = !loadingAuth && !loadingRole;

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentUserRole,
        setCurrentUserRole,
        refreshCurrentUser,
        logout,
        ready,
      }}
    >
      {ready ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
};
